import { useState, useEffect, useRef, useCallback } from "react";
import { DateRange, Calendar } from "react-date-range";
import { format, subDays, addDays, startOfMonth, endOfMonth, isSameDay } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useSelector } from "react-redux";

// Helper function to get last 30 days from today
const getLast30DaysRange = () => {
  const today = new Date();
  const endDate = subDays(today, 1);   
  const startDate = subDays(endDate, 29); 
  return { startDate, endDate };
};

// Helper function to get current month
const getCurrentMonthRange = () => {
  const today = new Date();
  return {
    startDate: startOfMonth(today),
    endDate: endOfMonth(today)
  };
};

// Helper function to get today
const getTodayRange = () => {
  const today = new Date();
  return {
    startDate: today,
    endDate: today
  };
};
const today = new Date();

const presets = [
  { label: "Yesterday", range: () => ({ startDate: subDays(new Date(), 1), endDate: subDays(new Date(), 1) }) },
  { label: "Last 7 Days", range: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }) },
  { label: "Last 15 Days", range: () => ({ startDate: subDays(new Date(), 14), endDate: new Date() }) },
  { label: "Last 30 Days", range: () => {const { startDate, endDate } = getLast30DaysRange(); return { startDate, endDate };}  },
  {
    label: "Last Month",
    range: () => {
      const now = new Date();
      const firstDay = startOfMonth(subDays(now, now.getDate()));
      const lastDay = endOfMonth(subDays(now, now.getDate()));
      return { startDate: firstDay, endDate: lastDay };
    },
  },
  { label: "Tomorrow", range: () => ({ startDate: addDays(new Date(), 1), endDate: addDays(new Date(), 1) }) },
  { label: "Next 7 Days", range: () => ({ startDate: new Date(), endDate: addDays(new Date(), 6) }) },
  { label: "Next 15 Days", range: () => ({ startDate: new Date(), endDate: addDays(new Date(), 14) }) },
  { label: "Next 30 Days", range: () => ({ startDate: new Date(), endDate: addDays(new Date(), 29) }) },
  {
    label: "Next Month",
    range: () => {
      const now = new Date();
      const firstDay = startOfMonth(addDays(endOfMonth(now), 1));
      const lastDay = endOfMonth(addDays(endOfMonth(now), 1));
      return { startDate: firstDay, endDate: lastDay };
    },
  },
];

const DateRangePickerStyle = ({
  onDateChange,
  initialRange,
  className = "h-[30px]",
  padding_css = "py-1",
  wrapperWidth = "w-[200px]",
  mode = "range", // 'range' or 'single'
  position_box = "right-0",
  disableInitialDateCall = false,
  defaultRangeType = "today", // NEW PROP: 'today', 'last30days', 'currentmonth'
}) => {
  const themeMode = useSelector((state) => state.theme.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";

  const [isOpen, setIsOpen] = useState(false);
  const [selectDate, setSelectDate] = useState(null);
  const pickerRef = useRef();

  // Default = today
  // const today = new Date();

  const [range, setRange] = useState([
    { startDate: today, endDate: today, key: "selection" },
  ]);
//   const [draftRange, setDraftRange] = useState([
//   { startDate: today, endDate: today, key: "selection" },
// ]);

  const [singleDate, setSingleDate] = useState(today);

  // Track previous dates to prevent duplicate calls
  const previousRangeRef = useRef({ startDate: today, endDate: today });
  const isInitialMountRef = useRef(true);
  const [position, setPosition] = useState("left-0");
  /** 🔹 Load initial values */
  useEffect(() => {
    if (mode === "range") {
      if (initialRange?.startDate && initialRange?.endDate) {
        // Use provided initialRange
        const start = new Date(initialRange.startDate);
        const end = new Date(initialRange.endDate);
        setRange([{ startDate: start, endDate: end, key: "selection" }]);
        setSelectDate({
          startDate: format(start, "yyyy-MM-dd"),
          endDate: format(end, "yyyy-MM-dd"),
        });
        previousRangeRef.current = { startDate: start, endDate: end };
        
        if (onDateChange && !disableInitialDateCall) {
          const formatted = {
            startDate: format(start, "yyyy-MM-dd"),
            endDate: format(end, "yyyy-MM-dd"),
          };
          onDateChange(formatted);
        }
      } else {
        // Set default based on defaultRangeType prop
        let defaultStartDate, defaultEndDate;
        
        switch (defaultRangeType) {
          case 'last30days':
            const last30Days = getLast30DaysRange();
            defaultStartDate = last30Days.startDate;
            defaultEndDate = last30Days.endDate;
            break;
            
          case 'currentmonth':
            const currentMonth = getCurrentMonthRange();
            defaultStartDate = currentMonth.startDate;
            defaultEndDate = currentMonth.endDate;
            break;
            
          case 'today':
          default:
            const todayRange = getTodayRange();
            defaultStartDate = todayRange.startDate;
            defaultEndDate = todayRange.endDate;
            break;
        }
        
        const formatted = {
          startDate: format(defaultStartDate, "yyyy-MM-dd"),
          endDate: format(defaultEndDate, "yyyy-MM-dd"),
        };
        
        setRange([{ startDate: defaultStartDate, endDate: defaultEndDate, key: "selection" }]);
        setSelectDate(formatted);
        previousRangeRef.current = { startDate: defaultStartDate, endDate: defaultEndDate };
        
        if (onDateChange && !disableInitialDateCall) {
          onDateChange(formatted);
        }
      }
    } else if (mode === "single") {
      if (initialRange?.startDate) {
        const date = new Date(initialRange.startDate);
        setSingleDate(date);
        setSelectDate({ date: format(date, "yyyy-MM-dd") });
        
        if (onDateChange && !disableInitialDateCall) {
          onDateChange({ date: format(date, "yyyy-MM-dd") });
        }
      } else {
        const formatted = { date: format(today, "yyyy-MM-dd") };
        setSelectDate(formatted);
        
        if (onDateChange && !disableInitialDateCall) {
          onDateChange(formatted);
        }
      }
    }
    
    isInitialMountRef.current = false;
  }, [initialRange, mode, defaultRangeType]);

  /** Smart date change handler - FIXED FOR SINGLE DAY SELECTION */
// useEffect(() => {
//   if (!onDateChange || isInitialMountRef.current) return;
//   if (mode !== "range") return;

//   const newStart = range[0].startDate;
//   const newEnd = range[0].endDate;

//   const prevStart = previousRangeRef.current.startDate;
//   const prevEnd = previousRangeRef.current.endDate;

//   // Same selection 
//   if (
//     isSameDay(newStart, prevStart) &&
//     isSameDay(newEnd, prevEnd)
//   ) {
//     return;
//   }

//   if (newStart && newEnd) {
//     const formatted = {
//       startDate: format(newStart, "yyyy-MM-dd"),
//       endDate: format(newEnd, "yyyy-MM-dd"),
//     };

//     setSelectDate(formatted);
//     onDateChange(formatted);

//     previousRangeRef.current = {
//       startDate: newStart,
//       endDate: newEnd,
//     };
//     //setTimeout(() => setIsOpen(false), 100);
//   }
// }, [range, mode, onDateChange]);

useEffect(() => {
  if (isOpen && pickerRef.current) {
    const containerRect = pickerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    // Check if dropdown would overflow on the right
    if (containerRect.right + 600 > viewportWidth) {
      setPosition("right-0");
    } else {
      setPosition("left-0");
    }
  }
}, [isOpen]);

  /** Close picker when clicking outside */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

// const handleRangeChange = useCallback((item) => {
//   setDraftRange([item.selection]);
// }, []);

  const handleSingleDateChange = (date) => {
    setSingleDate(date);
    setIsOpen(false);
  };

const applyPreset = useCallback((preset) => {
  const { startDate, endDate } = preset.range();

  setRange([{ startDate, endDate, key: "selection" }]);

  const formatted = {
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(endDate, "yyyy-MM-dd"),
  };
  setSelectDate(formatted);
  onDateChange?.(formatted);
  previousRangeRef.current = { startDate, endDate };

  setIsOpen(false);
}, [onDateChange]);

return (
  <div className="relative" ref={pickerRef}>
    {/* Trigger Button */}
    <button
      onClick={() => setIsOpen(!isOpen)}
      className={`${wrapperWidth} ${borderThem} ${textColorClass} ${className} border ${
        themeMode ? "bg-stone-700" : "bg-white"
      } transition-all duration-200 rounded-xl px-2 ${padding_css} text-xs md:text-sm`}
    >
      {mode === "range"
        ? `${selectDate?.startDate || "Start"} → ${selectDate?.endDate || "End"}`
        : selectDate?.date || "Select Date"}
    </button>

    {/* Popup Calendar */}
    {isOpen && (
<div
        className={`absolute ${position} z-50 mt-2 flex flex-col sm:flex-row rounded-2xl shadow-2xl bg-white border overflow-hidden ${bgColorClass} border ${borderThem}`}
>
        {/* Preset Buttons */}
        <div 
          className="w-full sm:w-40 p-2 border-b sm:border-b-0 sm:border-r sm:gap-1"
        >
          {presets.map((p, idx) => (
            <button
              key={idx}
              className="block w-full px-3 py-1 text-left text-sm rounded-lg"
              onClick={() => applyPreset(p)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* RIGHT SIDE */}
        <div className="flex flex-col w-fit">
          {/* Calendar */}
        <div className="overflow-x-auto w-fit">
            {mode === "range" ? (
              <DateRange
                editableDateInputs
               // onChange={handleRangeChange}
                moveRangeOnFirstSelection={false}
               // ranges={draftRange}
                maxDate={today}
                onChange={(item) => setRange([item.selection])}
                ranges={range}
                className={`custom-tailwind-picker border ${borderThem} rounded-xl shadow-sm`}

              />
            ) : (
              <Calendar
                date={singleDate}
                onChange={handleSingleDateChange}
                maxDate={today}
                 className={`custom-tailwind-picker border ${borderThem} rounded-xl shadow-sm`}
              />
            )}
          </div>

          {/* APPLY / CANCEL — THIS IS THE STOP */}
          {mode === "range" && (
          <div className="flex justify-end gap-2 p-3  ">
              <button
  className="px-3 py-1 text-sm rounded-md border"
  onClick={() => setIsOpen(false)}
>
  Cancel
</button>

              <button
  className="px-3 py-1 text-xs rounded-md bg-blue-600 text-white"
  onClick={() => {
    const { startDate, endDate } = range[0];

    const formatted = {
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    };

    setSelectDate(formatted);      // update button text
    onDateChange?.(formatted);     // send to booking report / API
    previousRangeRef.current = { startDate, endDate };

    setIsOpen(false);              // close picker
  }}
>
  Apply
</button>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);
};

export default DateRangePickerStyle;